/**
 * The graph as it is laid out on screen: positions, the force simulation, the view (pan and
 * zoom, 2D or 3D) and hit testing.
 *
 * The scene is a service (`useApp().scene`), not part of the graph window, so moving, docking
 * or floating the graph window keeps the layout and the zoom, and other windows (filters,
 * presets) can read it without importing the graph window. `sceneSync.ts` keeps it in step
 * with the core's graph. Node positions are kept by the core as part of the project
 * (`project.set_positions`), so they survive a reload and are saved in the file.
 *
 * It is one object made of parts, each in its own file with its own interface:
 *
 * | Part | File | What it does |
 * |---|---|---|
 * | {@link SceneData} | `types.ts` | The nodes, links, groups, bundles, view; shared types |
 * | {@link SceneContent} | `content.ts` | Following the graph, filters, saved positions |
 * | {@link SceneFollow} | `follow.ts` | Gliding to the layout shared by other people |
 * | {@link SceneLayout} | `forces.ts` | The force simulation |
 * | {@link SceneViewport} | `viewport.ts` | World ↔ screen, pan, zoom, fit, 3D perspective |
 * | {@link SceneDimension} | `dimension.ts` | The 2D / 3D switch |
 * | {@link SceneHits} | `hits.ts` | Nodes and links under the pointer |
 * | {@link SceneGroups} | `groupsOnScreen.ts` | Group circles, names, moving a group |
 * | {@link SceneBundles} | `bundlesOnScreen.ts` | Links between groups drawn as one |
 * | (helpers) | `folds.ts` | A folded group drawn as one disc standing in for its nodes |
 *
 * The parts call each other only through the assembled `scene` (`scene.layoutGroups()`,
 * `scene.sx(n)`...), never by importing each other's insides, so a part can be read and
 * changed on its own.
 *
 * Coordinates: *world* coordinates are where nodes live; *screen* coordinates are pixels in
 * the canvas (see `viewport.ts`).
 */
import { defaultCamera } from "../camera";
import { bundlesOnScreen, type SceneBundles } from "./bundlesOnScreen";
import { content, type SceneContent } from "./content";
import { dimensionSwitch, type SceneDimension } from "./dimension";
import { follow, type SceneFollow } from "./follow";
import { forces, type SceneLayout } from "./forces";
import { groupsOnScreen, type SceneGroups } from "./groupsOnScreen";
import { hits, type SceneHits } from "./hits";
import type { SceneData, SceneMemory } from "./types";
import { viewport, type SceneViewport } from "./viewport";

export type { Dimension, Focus, SceneBundle, SceneFilters, SceneGroup, SimLink, SimNode, ViewTransform } from "./types";
export { SPACING_RANGE, WARM_BUDGET_MS, WORKER_FROM, clampSpacing, prefersReducedMotion } from "./forces";
export { nodeRadius, segmentDistance } from "./geometry";
export { FOLD_PREFIX, foldedGroupOf, memberPlace, standInId } from "./folds";

/** The scene and everything that can be done with it: its data plus every part's functions. */
export interface Scene extends SceneData, SceneContent, SceneFollow, SceneLayout, SceneViewport, SceneDimension, SceneHits, SceneGroups, SceneBundles {}

/** Creates an empty scene. */
export function createScene(): Scene {
  const memory: SceneMemory = { hints: new Map(), flatLayout: null };
  const data: SceneData = {
    nodes: [],
    links: [],
    byId: new Map(),
    groups: [],
    groupById: new Map(),
    categories: [],
    showGroups: true,
    bundles: [],
    bundleByKey: new Map(),
    bundleOf: new Map(),
    view: { x: 0, y: 0, k: 1 },
    dimension: "2d",
    camera: defaultCamera(),
    width: 1,
    height: 1,
    alpha: 0,
    spacing: 1,
    appliedGeneration: -1,
    laidOut: false,
    lead: true,
    redraw: true,
  };
  // The parts get the finished scene: each part's functions are only called after this.
  const scene = data as Scene;
  Object.assign(scene, content(scene, memory), follow(scene, memory), forces(scene), viewport(scene), dimensionSwitch(scene, memory), hits(scene), groupsOnScreen(scene), bundlesOnScreen(scene));
  return scene;
}
